<template lang="pug">
  .container.width-100.m-top-5.m-bottom-5
    h3(v-if='!isSchoolChange').bold.m-bottom-2.text-xbig
      | {{ $t('register.titleNewUser') }}
    h3(v-else).bold.m-bottom-2.text-xbig
      | {{ $t('register.titleExistingUser') }}
    form.row(v-if='!!newUser' @submit.prevent='submit')
      .column.item-1
        .column.m-bottom-3(v-if='!withGoogle && !withMicrosoft')
          a.max-width-none.m-bottom-1.secondary-button.row(type='button' :href='googleLink')
            simple-svg.m-right-1(:filepath='googleGIcon' width='18px' height='18px')
            | {{ $t('register.continueWithGoogle') }}
          button.max-width-none.secondary-button.row(v-if='!hasSubdomain' type='button' @click='showMicrosoftPopup')
            simple-svg.m-right-1(:filepath='microsoftIcon' width='18px' height='18px')
            | {{ $t('register.continueWithMicrosoft') }}
          .row.middle.m-top-1(v-show='wrongCredentialsError')
            simple-svg.red-icon.m4-right-1(:filepath='stopIcon' :width='"16px"' :height='"16px"')
            span.error-desc {{ $t('errors.wrongCredentialsError') }}
        .divider.relative.width-100.m-bottom-3(v-if='!withGoogle && !withMicrosoft')
          span.absolute.text-normal.o O
        .form-group(v-if='!isSchoolChange || !hasFullName')
          label.text-normal
            | {{ $t('register.nameAndSurnameLabel') }}
          input.input-base.input-border(
            type='text' :disabled='withMicrosoft || withGoogle'
            name='fullName'
            :value='newUser.userVO.fullName'
            @input='updateFullname'
            :placeholder='$t("register.nameAndSurnamePlaceholder")'
            :class='{ error: showErrors && $v.newUser.userVO.fullName.$invalid, disabled: isSchoolChange && hasFullName, filled: newUser.userVO.fullName }'
          )
          .row.middle.m-top-1(v-if='showErrors && $v.newUser.userVO.fullName.$invalid')
            simple-svg.icon.red-icon.m4-right-1(:filepath='stopIcon' width='15px' height='15px')
            span.text-small.red.error-message
              | {{ $t('register.nameAndSurnameError') }}
        .form-group(v-if='isTeacherRegistration')
          label.text-normal.m4-bottom-2
            | {{ $t('register.levelLabel') }}
          tag-input(
            filled
            :placeholder='$t("register.levelPlaceholder")'
            :value='newUser.userVO.educationalLevels'
            :options='levels'
            @change='updateLevels'
            keyName='id'
            labelName='label'
            name='educationalLevels'
            :error='showErrors && !newUser.userVO.educationalLevels.length'
          )
          .row.middle.m-top-1(v-show='showErrors && !newUser.userVO.educationalLevels.length')
            simple-svg.red-icon.m4-right-1(:filepath='stopIcon' :width='"16px"' :height='"16px"')
            span.error-desc
              | {{ $t('register.educationalLevelsError') }}
        .form-group
          label.text-normal.m4-bottom-2
            | {{ $t("register.chargeLabel") }}
          k-select(
            name='position'
            :options='userPositions'
            @selectOption='updatePosition'
            :preselectedOption='hasValidPosition && newUser.userVO.position.positionId'
            :placeholder='$t("register.chargePlaceholder")'
            keyName='value'
            valueName='position'
            withBorder
            filled
            :error='showErrors && !newUser.userVO.position.positionId'
          )
          .row.middle.m-top-1(v-show='showErrors && !newUser.userVO.position.positionId')
            simple-svg.red-icon.m4-right-1(:filepath='stopIcon' :width='"16px"' :height='"16px"')
            span.error-desc
              | {{ $t('errors.positionRequiredError') }}
        .form-group(v-if='isTeacherRegistration')
          .row
            label.text-normal.m4-right-1.m4-bottom-2
              | {{ $t('register.subjectLabel') }}
            h2.text-normal.dark-gray.optional
              | {{ $t('register.optional') }}
          tag-input(
            filled
            name='teacherSubjects'
            :options='subjects'
            keyName='id'
            labelName='name'
            :placeholder='$t("register.subjectPlaceholder")'
            :value='newUser.userVO.teacherSubjects'
            @change='updateSubjects'
          )
        .form-group
          .row.m4-bottom-2
            label.text-normal.m4-right-1
              | {{ $t('register.phoneLabel') }}
            h2.text-normal.dark-gray.optional
              | {{ $t('register.optional') }}
          telephone-input(
            filled
            name='cellPhoneNumber'
            :value='newUser.userVO.cellPhoneNumber'
            @change='updateCellPhoneNumber'
            :placeholder='$t("register.phonePlaceholder")'
            autocomplete='off'
          )
        .form-group(v-if='!withGoogle && !withMicrosoft')
          label.text-normal
            | {{ $t('register.passwordLabel') }}
          password-validator(
            :value='newUser.password'
            @updateValue='updatePassword'
            :showErrors='showErrors'
            :validations='$v.newUser.password'
            filled
          )
        .form-group(v-if='!withGoogle && !withMicrosoft')
          label.text-normal
            | {{ $t('register.replacePasswordLabel') }}
          hidden-form-input(
            type='text'
            name='confirmPassword'
            :value='newUser.confirmPassword'
            @input='updatePassword'
            :placeholder='$t("register.replacePasswordPlaceholder")'
            filled
            autocomplete='off'
            :inputError='showErrors && $v.newUser.confirmPassword.$invalid'
          )
          .row.middle.m-top-1(v-if='showErrors && $v.newUser.confirmPassword.$invalid')
            simple-svg.icon.red-icon.m4-right-1(:filepath='stopIcon' width='15px' height='15px')
            span.text-small.red
              | {{ confirmPasswordMessage }}
        .form-group.column.m4-bottom-10
          .row
            label.row.middle
              input.checkbox.absolute(
                type='checkbox'
                name='termsAndConditions'
                v-model='termsAndConditions'
              )
              span.checkmark.m4-right-2
              span.text-smal.m4-right-1
                | {{ $t('register.accept') }}
            button.button-simple.button.terms-and-conditions-button(type='button' @click='handleTermsAndConditions')
              | {{ $t('register.termsAndConditions') }}
          .row.middle.span-error-txt.m4-top-2(v-show='showErrors && !termsAndConditions')
            simple-svg.icon.red-icon.m4-right-1(:filepath='stopIcon' :width='"15px"' :height='"15px"')
            span.text-small.red
              | {{ $t('errors.termsAndConditions') }}
        button.primary-button.submit-button(type='submit' :disabled='!termsAndConditions')
          | {{ $t('register.button') }}
      .column.item-1
        .form-group
          .row
            .profile-img.relative
              img.m-bottom-4(:src='avatar || defaultImg' alt='user avatar' )
              label(v-if='!isSchoolChange')
                simple-svg.add-img(:filepath='cameraIcon' width='30px' height='30px')
                input.hide(ref='imageInput' id='user-image' name='userImage' type='file' @change='handleSubmitImage' accept="image/png,image/jpg,image/jpeg")
            .column
              h1.m-left-2.title-medium {{ newUser.userVO.fullName }}
              .column.m-top-auto
                h1.m4-bottom-2.m-left-2.text-big.dark-gray(
                  v-if='newUser.userVO.position.positionId && hasValidPosition'
                ) {{ getPosition(newUser.userVO.position.positionId) }}
                h1.m-left-2.text-big.dark-gray {{ newUser.schoolName }}
          span.text-normal.red.error-message.m-top-2(v-if='imageError')
            | {{ $t('register.photoError') }}
        .form-group
          .row
            label.text-normal.m4-right-1
              | {{ $t('register.bioLabel') }}
            h2.text-normal.dark-gray.optional
              | {{ $t('register.optional') }}
          textarea.input-border.black.bio(
            type='text'
            name='bio'
            :value='newUser.userVO.bio'
            @input='updateBio'
            :placeholder='$t("register.bioPlaceholder")'
            :class='{ error: showErrors && $v.newUser.userVO.bio.$invalid, filled: newUser.userVO.bio }'
            :maxLength='400'
          )
          h2.text-normal.dark-gray.m-top-1.characters(v-if='false' :class='{ red: showErrors && $v.newUser.userVO.bio.$invalid }')
            | {{ bio.length + ' '}}{{ $t('register.characters') }}
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { required, sameAs, maxLength, minLength } from 'vuelidate/lib/validators'
import get from 'lodash/get'
import amplitude from 'amplitude-js'

import KSelect from '@/components/KSelect'
import PasswordValidator from '@/components/Register/PasswordValidator'
import TagInput from '@/components/Inputs/TagInput'
import TelephoneInput from '@/components/Inputs/TelephoneInput'
import HiddenFormInput from '@/components/HiddenFormInput'

import stopIcon from '@/assets/ic_stop.svg'
import defaultImg from '@/assets/profile_default_img.png'
import googleGIcon from '@/assets/ic_google_g.svg'
import microsoftIcon from '@/assets/ic_microsoft.svg'
import cameraIcon from '@/assets/ic_camera_add.svg'
import { toLowerCamelCase } from '@/utils/string'
import { firstElement, charactersLimit, maxFileSize, userPrimaryRoles } from '../utils/constants'

import { routes } from '@/router'
import { getBase64, getExtension, isImage } from '../utils/file.js'
import { i18n } from '../main'
import { MSAL } from '@/config/msalConfig'
import { getGoogleAccessToken, getInviteTokenFromHash } from '@/utils/url.js'
import { getParamInRouteHash } from '../utils/url'

export default {
  components: { KSelect, PasswordValidator, TagInput, TelephoneInput, HiddenFormInput },
  data() {
    return {
      cameraIcon,
      microsoftIcon,
      googleGIcon,
      defaultImg,
      showErrors: false,
      stopIcon,
      termsAndConditions: false,
      isSchoolChange: false,
      maxFileSize,
      imageError: false,
      hasFullName: false,
      externalToken: null,
      withMicrosoft: false,
      withGoogle: false,
      wrongCredentialsError: false
    }
  },
  validations() {
    return !this.isTeacherRegistration ? {
      newUser: {
        password: (this.withGoogle || this.withMicrosoft) ? {} : {
          required,
          minLength: (value) => value.length >= 6,
          mustHaveUpperAndLower: (value) => /(?=.*[a-z])(?=.*[A-Z])/.test(value),
          mustHaveANumber: (value) => /\d/.test(value)
        },
        confirmPassword: (this.withGoogle || this.withMicrosoft) ? {} : {
          required,
          sameAsPassword: sameAs('password')
        },
        userVO: {
          bio: { maxLength: maxLength(charactersLimit) },
          fullName: { required },
          position: {
            positionId: { required }
          }
        }
      }
    } : {
      newUser: {
        password: (this.withGoogle || this.withMicrosoft) ? {} : {
          required,
          minLength: (value) => value.length >= 6,
          mustHaveUpperAndLower: (value) => /(?=.*[a-z])(?=.*[A-Z])/.test(value),
          mustHaveANumber: (value) => /\d/.test(value)
        },
        confirmPassword: (this.withGoogle || this.withMicrosoft) ? {} : {
          required,
          sameAsPassword: sameAs('password')
        },
        userVO: {
          bio: { maxLength: maxLength(charactersLimit) },
          fullName: { required },
          position: {
            positionId: { required }
          },
          educationalLevels: { required, minLength: minLength(1) }
        }
      }
    }
  },
  computed: {
    ...mapState({
      newUser: state => state.profile.newUser,
      hasSubdomain: state => state.whiteLabel.hasSubdomain,
      showUserError: state => state.userInfo.showInvalidUserError,
      showTrialInviteModal: state => state.behavior.trialInviteModal.show,
      positions: state => get(state.profile, 'userPositions') || [],
      subjects: state => get(state.profile, 'subjects') || [],
      avatar: state => get(state.profile, 'newUser.userVO.avatar') || '',
      educationalLevels: state => state.profile.educationalLevels,
      googleUserInfo: state => state.userInfo.googleUserInfo,
      microsoftUserInfo: state => state.userInfo.microsoftUserInfo
    }),
    googleLink() {
      const { host, protocol } = window.location
      const clientId = process.env.VUE_APP_GOOGLE_CLIENT_ID
      const redirectUri = host.split('.').length > 2
        ? `${protocol}//${host.split('.')[1]}.com/register`
        : `${protocol}//${host}/register`
      const responseType = 'token'
      const state = this.$route.hash
        ? getParamInRouteHash(this.$route, 'state')
        : this.$route.query.inviteToken
      const scope = 'email profile'
      return `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=${responseType}&scope=${scope}&state=${state}`
    },
    userPositions() {
      return this.positions
        .filter(pos => pos.positionId)
        .map(level => (
          { value: level.positionId, label: this.$t(`profile.position.${toLowerCamelCase(level.position)}`) }
        ))
        .sort((a, b) => a.label.localeCompare(b.label))
    },
    confirmPasswordMessage() {
      const { required } = this.$v.newUser.confirmPassword
      return !required ? this.$t('register.replacePasswordError') : this.$t('register.replacePasswordDifferentError')
    },
    isTeacherRegistration() {
      return this.newUser.invitationRole.name === userPrimaryRoles.teacher
    },
    levels() {
      return this.educationalLevels
        .filter(level => this.newUser.schoolEducationalLevels.includes(level.id))
        .map(level => ({ ...level, label: i18n.t(`educationalLevels.${level.name.toLowerCase()}`) }))
    },
    hasValidPosition() {
      return !!this.positions.find(pos => pos.positionId === this.newUser.userVO.position.positionId)
    }
  },
  async created() {
    const inviteToken = this.$route.query.inviteToken || getInviteTokenFromHash(this.$route.hash) || null
    await MSAL.initialize()
    await this.hydrateNewUser(inviteToken)
    if (!this.$route.query.inviteToken) this.setGoogleData()
    if (this.isTeacherRegistration) {
      this.changeNewUserVOField({ field: 'educationalLevels', value: this.newUser.invitationEducationalLevel })
    }
    if (this.newUser.userVO.fullName) {
      this.hasFullName = true
    } else {
      this.hasFullName = false
    }
    const invitedUserId = this.$route.query.invitedUserId
    if (invitedUserId && invitedUserId === this.newUser.userVO.userId) {
      this.isSchoolChange = true
    }
    if (!this.hasValidPosition) {
      this.updatePosition('position', null)
    }
    this.hydrateEducationalLevels()
    this.hydrateNewUserPositions(this.newUser.invitationRole.name)
    this.hydrateSubjectsNewUser()
  },
  methods: {
    ...mapActions([
      'hydrateNewUser',
      'showElement',
      'hideElement',
      'hydrateSubjectsNewUser',
      'hydrateEducationalLevels',
      'hydrateNewUserPositions',
      'submitNewUser',
      'submitNewExternalUser',
      'submitSchoolChange',
      'login',
      'externalGoogleLogin',
      'externalMicrosoftLogin',
      'getLoggedUserInfo',
      'changeNewUserField',
      'changeNewUserVOField',
      'changeNewUserPosition',
      'getExternalMicrosoftInfo',
      'getExternalGoogleInfo',
      'uploadImage'
    ]),
    handleTermsAndConditions() {
      this.showElement({ element: 'termsAndConditionsModal' })
    },
    showMicrosoftPopup() {
      MSAL.loginPopup({ scopes: ['User.Read'] })
        .then(async response => {
          if (response) this.setMicrosoftData(response.accessToken)
        })
        .catch((error) => console.error(error))
    },
    async setMicrosoftData(token) {
      this.showElement({ element: 'fullPageLoader' })
      if (token) await this.getExternalMicrosoftInfo(token)
      this.hideElement({ element: 'fullPageLoader' })
      if (this.microsoftUserInfo.mail === this.newUser.userVO.email) {
        this.changeNewUserVOField({ field: 'fullName', value: this.microsoftUserInfo.displayName })
        this.withMicrosoft = true
        this.externalToken = token
      } else {
        this.wrongCredentialsError = true
      }
    },
    async setGoogleData() {
      this.showElement({ element: 'fullPageLoader' })
      const token = getGoogleAccessToken(this.$route)
      await this.getExternalGoogleInfo(token)
      this.hideElement({ element: 'fullPageLoader' })
      if (this.googleUserInfo.email === this.newUser.userVO.email) {
        this.changeNewUserVOField({ field: 'fullName', value: this.googleUserInfo.name })
        this.withGoogle = true
        this.externalToken = token
      } else {
        this.wrongCredentialsError = true
      }
    },
    async submit() {
      this.showErrors = false
      if (this.$v.$invalid || !this.termsAndConditions) {
        this.showErrors = true
      } else {
        if (this.withMicrosoft || this.withGoogle) await this.submitNewExternalUser()
        else if (this.isSchoolChange && this.newUser.inviteToken) {
          const userInvitation = {
            ...this.newUser,
            inviteToken: this.$route.query.inviteToken
          }
          await this.submitSchoolChange(userInvitation)
        } else {
          const success = await this.submitNewUser()
          if (!success) this.$router.push({ name: routes.usedToken })
        }

        if (this.withGoogle) {
          const success = await this.externalGoogleLogin(this.externalToken)
          if (success) {
            await this.getLoggedUserInfo()
            this.$router.push({ name: routes.feed })
          }
        } else if (this.withMicrosoft) {
          const success = await this.externalMicrosoftLogin(this.externalToken)
          if (success) {
            await this.getLoggedUserInfo()
            this.$router.push({ name: routes.feed })
          }
        } else {
          const { email } = this.newUser.userVO
          const { password } = this.newUser
          amplitude.getInstance().logEvent('Login')
          await this.login({ email, password })
          if (!this.showUserError) {
            await this.getLoggedUserInfo()
            this.$router.push({ name: routes.feed })
          }
        }
      }
    },
    async handleSubmitImage(event) {
      this.imageError = false
      const file = event.target.files[firstElement]
      const extension = getExtension(file.name)
      if (file.size > this.maxFileSize && isImage(extension)) {
        this.imageError = true
        this.$refs.imageInput.value = null
        return
      }
      const base64 = await getBase64(file)
      const url = await this.uploadImage({
        content: base64,
        extension
      })
      this.$refs.imageInput.value = null
      this.changeNewUserVOField({ field: 'avatar', value: url })
    },
    updateFullname(e) {
      this.changeNewUserVOField({ field: e.target.name, value: e.target.value })
    },
    updatePosition(field, value) {
      this.changeNewUserPosition({ field, value })
    },
    updateSubjects(value, field) {
      this.changeNewUserVOField({ field, value })
    },
    updateCellPhoneNumber(value, field) {
      this.changeNewUserVOField({ field, value })
    },
    updatePassword(value, field) {
      this.changeNewUserField({ field, value })
    },
    updateBio(e) {
      this.changeNewUserVOField({ field: e.target.name, value: e.target.value })
    },
    getPosition(positionId) {
      const label = this.positions.find(pos => pos.positionId === positionId).position
      return i18n.t(`profile.position.${toLowerCamelCase(label)}`)
    },
    updateLevels(level, field) {
      this.changeNewUserVOField({ field, value: level })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../scss/variables/colors';
@import '../scss/variables/sizes';

.container {
  max-width: 1024px;
  margin-right: auto;
  margin-left: auto;
}

.divider{
  border-bottom: 1px solid $gray;
  .o {
    right: 50%;
    background: $white;
    border: 6px solid $white;
    transform: translate(50%, -50%);
  }
}

form{
  &>:nth-child(1){
    margin-right: 50px;
  }
  &>:nth-child(2){
    margin-left: 50px;
  }
  .column{
    &>.form-group:not(:last-child){
      margin-bottom: 20px;
    }
  }
}

.filled{
  background-color: $lighter-blue;
}

.submit-button {
  --max-width: 270px;
}

.checkmark {
  background-color: $white;
  border: 1px solid $gray;
  border-radius: 6px;
  box-sizing: border-box;
  height: 21px;
  width: 21px;
}

.checkbox {
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkbox:checked ~ .checkmark {
  background: $blue url('../assets/ic_check-incircle.svg');
  border: none;
}

.terms-and-conditions-button {
  text-transform: none;
}

.profile-img {
  img {
    border-radius: 100%;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  width: 100px;
  height: 100px;
  .add-img {
    position: absolute;
    bottom: 0;
    right: 0;
    cursor: pointer;
  }
}

.bio {
  min-height: 100px;
  padding: 10px;
  resize: vertical;
}

@media screen and (max-width: $tablet-break){
  .main-container{
    padding-left: 25px;
    padding-right: 25px;
  }
  form{
    flex-direction: column;
    &>:nth-child(1){
      margin-right: 0;
      order: 1;
    }
    &>:nth-child(2){
      margin-left: 0;
      margin-bottom: 20px;
    }
  }
  .submit-button{
    max-width: none;
  }
}

@media screen and (max-width: $mobile-break){
  .profile-img{
    width: 70px;
    height: 70px;
  }
}

</style>
